'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { cn } from '@/lib/ui-kit/utils';
import { formatCents } from '@/lib/utils/formatAmount';
import { LABEL_CLASS, Z_OVERLAY_MAX } from '../constants';
import type { PriceAlertSide } from '../types';
import { useOutcomeLiveCents } from '../utils';
import { SideToggle } from './SideToggle';

interface OutcomeAndSideFieldProps {
  outcomes: MarketItem[];
  outcomeId: string;
  onOutcomeIdChange: (id: string) => void;
  side: PriceAlertSide;
  onSideChange: (side: PriceAlertSide) => void;
}

export function OutcomeAndSideField({
  outcomes,
  outcomeId,
  onOutcomeIdChange,
  side,
  onSideChange,
}: OutcomeAndSideFieldProps) {
  const tf = useTranslations('priceAlerts.form');
  const [pickerOpen, setPickerOpen] = useState(false);

  const selected = outcomes.find((o) => o.id === outcomeId) ?? outcomes[0];
  const { yesCents, noCents } = useOutcomeLiveCents(selected);
  const currentCents = side === 'YES' ? yesCents : noCents;

  if (outcomes.length <= 1) {
    return (
      <div className="flex flex-col gap-2">
        <span className={LABEL_CLASS}>{tf('outcome')}</span>
        <SideToggle
          side={side}
          onChange={onSideChange}
          yesCents={yesCents}
          noCents={noCents}
          fullWidth
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-[800px]:flex-row min-[800px]:items-end">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <label htmlFor="pa-outcome" className={LABEL_CLASS}>
          {tf('outcome')}
        </label>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              id="pa-outcome"
              aria-label={tf('outcome')}
              className="group flex h-10 w-full cursor-pointer items-center justify-between rounded-[10px] bg-v2-btn-secondary px-4 outline-none data-[state=open]:ring-1 data-[state=open]:ring-v2-border"
            >
              <span className="truncate text-[13px] leading-[150%] text-v2-text-primary">
                {selected?.groupItemTitle ?? ''}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <span className="text-[13px] leading-[150%] text-v2-text-secondary">
                  {formatCents(currentCents)}
                </span>
                <Icon
                  name="ArrowSmallDown"
                  size={20}
                  className="text-v2-text-secondary transition-transform group-data-[state=open]:rotate-180"
                />
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={18}
            zIndex={Z_OVERLAY_MAX}
            className="flex w-[var(--radix-popover-trigger-width)] flex-col gap-2 rounded-[10px] border border-v2-border bg-v2-surface-primary p-3 shadow-md dark:bg-v2-btn-secondary dark:shadow-none"
          >
            {outcomes.map((o) => (
              <OutcomePickerRow
                key={o.id}
                outcome={o}
                side={side}
                isSelected={o.id === outcomeId}
                onSelect={() => {
                  onOutcomeIdChange(o.id);
                  setPickerOpen(false);
                }}
              />
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <SideToggle side={side} onChange={onSideChange} yesCents={yesCents} noCents={noCents} />
    </div>
  );
}

interface OutcomePickerRowProps {
  outcome: MarketItem;
  side: PriceAlertSide;
  isSelected: boolean;
  onSelect: () => void;
}

function OutcomePickerRow({ outcome, side, isSelected, onSelect }: OutcomePickerRowProps) {
  const { yesCents, noCents } = useOutcomeLiveCents(outcome);
  const cents = side === 'YES' ? yesCents : noCents;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex cursor-pointer items-center justify-between py-1 text-left outline-none"
    >
      <span
        className={cn(
          'truncate',
          isSelected
            ? 'text-[13px] leading-4 font-[500] text-v2-text-primary'
            : 'text-[13px] leading-[150%] font-normal text-v2-text-secondary',
        )}
      >
        {outcome.groupItemTitle}
      </span>
      <span className="ml-2 shrink-0 text-[13px] leading-[150%] font-normal text-v2-text-secondary">
        {formatCents(cents)}
      </span>
    </button>
  );
}
